import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaChevronRight,
  FaClock,
  FaComments,
  FaPlus,
  FaSearch,
  FaSpinner,
} from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import useApp from "../../hooks/useAppContext";
import useAuth from "../../hooks/useAuthContext";
import { formatarDiaHora } from "../../services/conversaoTimer";
import "../adm/admPatrocinadoresList.css";
import "./chat.css";

function timestampMensagem(valor) {
  if (!valor) return 0;
  const data = new Date(valor);
  return Number.isNaN(data.getTime()) ? 0 : data.getTime();
}

const Chat = () => {
  const navigate = useNavigate();
  const { getMensagens, handleImagemParticipante } = useApp();
  const { usuario } = useAuth();
  const [conversas, setConversas] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const carregarConversas = useCallback(async () => {
    if (!usuario?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      const dados = await getMensagens();
      const lista = (dados?.conversas || []).map((c) => {
        const baseUser =
          c.id_origem === usuario.id ? c.id_destino : c.id_origem;
        const user = (dados?.usuarioConversa || []).find(
          (uc) => uc.id === baseUser
        );
        return { ...c, usuario: user || null };
      });

      lista.sort((a, b) => {
        const naoLidasA = a?.chatMensagens?.length || 0;
        const naoLidasB = b?.chatMensagens?.length || 0;
        if (naoLidasA !== naoLidasB) return naoLidasB - naoLidasA;

        const dataA = timestampMensagem(a?.ultima_mensagem_em);
        const dataB = timestampMensagem(b?.ultima_mensagem_em);
        if (dataA !== dataB) return dataB - dataA;

        return String(a?.usuario?.nome || "").localeCompare(
          String(b?.usuario?.nome || ""),
          "pt-BR"
        );
      });

      setConversas(lista);
    } catch (err) {
      console.error("Erro ao carregar conversas:", err);
      setError("Erro ao carregar suas conversas");
      setConversas([]);
    } finally {
      setIsLoading(false);
    }
  }, [getMensagens, usuario]);

  useEffect(() => {
    carregarConversas();
  }, [carregarConversas]);

  const conversasFiltradas = useMemo(() => {
    if (!filtro.trim()) return conversas;
    const termo = filtro.toLowerCase().trim();
    return conversas.filter((c) => {
      const campos = [
        c?.usuario?.nome,
        c?.usuario?.empresa,
        c?.usuario?.cargo,
      ];
      return campos.some((campo) =>
        String(campo || "")
          .toLowerCase()
          .includes(termo)
      );
    });
  }, [conversas, filtro]);

  const totalNaoLidas = useMemo(
    () =>
      conversas.reduce(
        (acc, c) => acc + (c?.chatMensagens?.length || 0),
        0
      ),
    [conversas]
  );

  return (
    <div className="main">
      <div className="corpo-pagina chat-page">
        <div className="apl-page">
          <header className="apl-header" aria-label="Cabeçalho das mensagens">
            <div className="apl-header-text">
              <h1>Mensagens</h1>
              <p>
                Acesse suas conversas com participantes e acompanhe mensagens
                não lidas.
              </p>
            </div>

            <div className="apl-toolbar apl-toolbar--inline">
              <div className="apl-search">
                <FaSearch className="apl-search-icon" aria-hidden />
                <input
                  type="search"
                  className="apl-search-input"
                  placeholder="Buscar conversa…"
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                  aria-label="Buscar conversa"
                />
              </div>
              <Link
                to="/private/participantes"
                className="apl-btn apl-btn--primary"
                title="Nova mensagem"
              >
                <FaPlus size={14} aria-hidden />
                Nova mensagem
              </Link>
              <button
                type="button"
                className="apl-btn apl-btn--ghost"
                onClick={() => navigate(-1)}
                title="Voltar"
              >
                <FaArrowLeft size={14} aria-hidden />
                Voltar
              </button>
            </div>
          </header>

          <div className="body-tela-adm" style={{ paddingBottom: 0 }}>
            {error ? (
              <div className="apl-alert" role="alert">
                {error}
              </div>
            ) : null}

            <section className="apl-card" aria-label="Lista de conversas">
              {isLoading ? (
                <div className="apl-loading">
                  <FaSpinner className="fa-spin" size={28} aria-hidden />
                  <span>Carregando conversas…</span>
                </div>
              ) : conversas.length === 0 ? (
                <div className="apl-empty">
                  <p className="apl-empty-title">Nenhuma conversa ainda</p>
                  <p style={{ margin: 0 }}>
                    Quando você trocar mensagens com outros participantes, elas
                    aparecerão aqui.
                  </p>
                </div>
              ) : conversasFiltradas.length === 0 ? (
                <div className="apl-empty">
                  <p className="apl-empty-title">Nenhuma conversa encontrada</p>
                  <p style={{ margin: 0 }}>
                    Ajuste a busca para encontrar o participante desejado.
                  </p>
                </div>
              ) : (
                <>
                  <ul className="chat-list" aria-label="Conversas">
                    {conversasFiltradas.map((m) => {
                      const naoLidas = m?.chatMensagens?.length || 0;
                      const temNaoLidas = naoLidas > 0;
                      const dataUltima = m?.ultima_mensagem_em
                        ? formatarDiaHora(String(m.ultima_mensagem_em))
                        : "";
                      const destino = m?.usuario?.id
                        ? `/private/chat/${m.usuario.id}`
                        : null;

                      if (!destino) return null;

                      return (
                        <li key={m.id}>
                          <Link
                            to={destino}
                            className={`chat-item${
                              temNaoLidas ? " is-unread" : ""
                            }`}
                          >
                            <img
                              className="chat-item-avatar"
                              src={handleImagemParticipante(m.usuario)}
                              alt=""
                            />
                            <div className="chat-item-body">
                              <div className="chat-item-top">
                                <strong className="chat-item-nome">
                                  {m?.usuario?.nome || "Participante"}
                                </strong>
                                {temNaoLidas ? (
                                  <span className="chat-item-badge">
                                    {naoLidas > 99 ? "99+" : naoLidas}
                                  </span>
                                ) : null}
                              </div>
                              {(m?.usuario?.cargo || m?.usuario?.empresa) && (
                                <span className="chat-item-meta">
                                  {[m.usuario.cargo, m.usuario.empresa]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </span>
                              )}
                              <span className="chat-item-status">
                                {dataUltima ? (
                                  <>
                                    <FaClock size={11} aria-hidden />
                                    Última mensagem: {dataUltima}
                                  </>
                                ) : (
                                  <>
                                    <FaComments size={11} aria-hidden />
                                    Sem mensagens
                                  </>
                                )}
                                {temNaoLidas ? (
                                  <span className="chat-item-status-unread">
                                    · {naoLidas}{" "}
                                    {naoLidas === 1 ? "não lida" : "não lidas"}
                                  </span>
                                ) : null}
                              </span>
                            </div>
                            <FaChevronRight
                              className="chat-item-arrow"
                              size={12}
                              aria-hidden
                            />
                          </Link>
                        </li>
                      );
                    })}
                  </ul>

                  <footer className="apl-footer">
                    <span>
                      Exibindo <strong>{conversasFiltradas.length}</strong>{" "}
                      {conversasFiltradas.length === 1
                        ? "conversa"
                        : "conversas"}
                      {filtro.trim() ? ` de ${conversas.length}` : ""}
                    </span>
                    <span>
                      {totalNaoLidas > 0
                        ? `${totalNaoLidas} ${
                            totalNaoLidas === 1 ? "não lida" : "não lidas"
                          }`
                        : "Tudo lido"}
                    </span>
                  </footer>
                </>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;
